import { Button, TextField, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import axios from "axios";
import { Fragment, MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DropDownButton, { DropDownMenu } from "../../../components/shared/DropDownButton";
import useSessionData from "../../../hooks/useSessionData";
import { User } from "../../../model/auditlogreports";
import MultiItemSelectAutoComplete from "./MultiItemSelectAutoComplete";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";

const reportTypeMenus: DropDownMenu[] = [
    { label: 'Approver Enrollments', value: 'approverEnrollments' },
    { label: 'User Enrollments', value: 'userEnrollments' },
    { label: 'Log-in / Log-out', value: 'loginLogout' },
    { label: 'Threshold Changes', value: 'thresholdChanges' }
];
export const timeFilterMenus: DropDownMenu[] = [
    { label: 'Last 7 days', value: 'last7Days' },
    { label: 'Last 14 days', value: 'last14Days' },
    { label: 'Last 30 days', value: 'last30Days' },
    { label: 'Month-to-date', value: 'monthToDate' },
    { label: 'Last 90 days', value: 'last90Days' },
    { label: 'Quarter-to-date', value: 'quarterToDate' },
    { label: 'Year-to-date', value: 'yearToDate' },
    { label: 'Custom Dates', value: 'customDates' }
];

const ReportCriteriaSelection = ({ setReportType, setFilterBy, setCustomStartDate, setCustomEndDate, setSelectedUsers, canEnableSubmit, onSubmitReport }: reportCriteriaSelectionProps) => {    
    const [users, setUsers] = useState<User[]>([{id: 'allUsers', name: 'All Users'}]);
    const [value, setValue] = useState<User[]>([]);
    const [filter, setFilter] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [pendingValue, setPendingValue] = useState<User[]>([]);
    const { t } = useTranslation();   
    const session = useSessionData();

    useEffect(() => {
        const fetchUsers = async () => {
            const accountId = session?.info?.accountID || '';
            const { data } = await axios.get(`/FaceFirst/FF?ACTION=queryUser&account_id=${accountId}&V=`);

            data.userinfo.users.sort((a: any, b: any) => {
                const nameA = a.contact.lastName.toUpperCase();
                const nameB = b.contact.lastName.toUpperCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            });

            const usersData = data.userinfo.users.map((user: { contact: { lastName: any; firstName: any; }; userID: any; username: any }) => {
                let name = (user?.contact?.firstName === '' || user?.contact?.lastName === '') ? user?.username : `${user?.contact?.lastName}, ${user?.contact?.firstName}`;

                return {
                    name: name,
                    id: user?.userID
                }
            });
            
            let userArr = [...users, ...usersData];
            setUsers(userArr);
        }
        if (users.length == 1) {
            fetchUsers();
        }
    });

    const setValueUpdateUsers = (users: User[]) => {
        setValue(users);
        setSelectedUsers(users);
    }
    
    const handleStartDate = (newValue: any) => {
        setStartDate(newValue);
        setCustomStartDate(newValue.format('YYYY-MM-DDTHH:mm:ss.SSS'));
    };
    const handleEndDate = (newValue: any) => {
        setEndDate(newValue);
        setCustomEndDate(newValue.format('YYYY-MM-DDTHH:mm:ss.SSS'));
    };
    const handleFilterByChange = (newFilter: string) => {
        if(newFilter !== 'customDates') {
            setStartDate('');
            setEndDate('');
        }
        setFilterBy(newFilter); 
        setFilter(newFilter);
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Fragment>
                <Stack direction={"row"} spacing={4}>
                    <Stack
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: 2,
                            boxShadow: theme => '0px 0px 8px ' + theme.palette.grey.A200,
                            alignItems: 'center',
                            alignContent: 'center',
                            minWidth: '20rem'
                        }}>
                        <Typography sx={{
                            pl: 1,
                            color: (theme) => theme.palette.secondary.main,
                            fontWeight: 'bold',
                            fontSize: 14,
                        }}>
                            Report type:
                        </Typography>
                        <DropDownButton
                            width={'160px'}
                            boxShadow={false}
                            fontSize={14}
                            data={reportTypeMenus}
                            color='primary.main'
                            fontWeight='bold'
                            handler={(e: MouseEvent<HTMLElement>, val: string) => setReportType(val)}
                        />
                    </Stack>
                    <Stack direction={"column"} spacing={2}>
                        <Stack
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: 2,
                                boxShadow: theme => '0px 0px 8px ' + theme.palette.grey.A200,
                                alignItems: 'center',
                                minWidth: '20rem'
                            }}>
                            <Typography sx={{
                                pl: 1,
                                color: (theme) => theme.palette.secondary.main,
                                fontWeight: 'bold',
                                fontSize: 14,
                            }}>
                                Filter by:
                            </Typography>
                            <DropDownButton
                                width={'160px'}
                                boxShadow={false}
                                fontSize={14}
                                data={timeFilterMenus}
                                color='primary.main'
                                fontWeight='bold'
                                handler={(e: MouseEvent<HTMLElement>, val: string) => { handleFilterByChange(val)}}
                            />
                            {filter == 'customDates' && <Stack direction={"row"} spacing={4}>
                                <DateTimePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(newValue) => handleStartDate(newValue)}
                                />
                                <DateTimePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={(newValue) => handleEndDate(newValue)}
                                />
                            </Stack>}
                        </Stack>

                    </Stack>
                    <Stack
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: 2,
                            boxShadow: theme => '0px 0px 8px ' + theme.palette.grey.A200,
                            alignItems: 'center',
                            minWidth: '20rem'
                        }}>
                        <Typography sx={{
                            pl: 1,
                            color: (theme) => theme.palette.secondary.main,
                            fontWeight: 'bold',
                            fontSize: 14,
                        }}>
                            Users:
                        </Typography>
                        <MultiItemSelectAutoComplete users={users} value={value} pendingValue={pendingValue} setValueUpdateUsers={setValueUpdateUsers} setPendingValue={setPendingValue} />
                    </Stack>
                    <Stack
                    >
                        <Button
                            variant="contained"
                            disabled={canEnableSubmit}
                            onClick={onSubmitReport}
                            style={canEnableSubmit ? { backgroundColor: 'grey', color: '#fff', width: '300px', padding: '12px' } : { backgroundColor: '#1976d2', width: '300px', padding: '12px' }}
                        >
                            {t('submit_report')}
                        </Button>
                    </Stack>
                </Stack>
            </Fragment>
        </LocalizationProvider>
    )
}
type reportCriteriaSelectionProps = {
    setReportType: any
    setFilterBy: any
    setCustomStartDate: any
    setCustomEndDate: any
    setSelectedUsers: any
    canEnableSubmit: boolean
    onSubmitReport: () => void;
}

export default ReportCriteriaSelection;